import {
    activityGoalMetrics,
    GoalMetric,
    goalMetricLabels,
    goalMetrics,
    MarketingGoalTargets,
    outcomeGoalMetrics,
    UserId,
    zeroTargets,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import {
    currentYearMonth,
    goalMetricBarColor,
    ymAddMonths,
    ymLabel,
} from "components/marketing-goals/pace";
import { trpc } from "components/rpc/trpc";
import { DeleteButton } from "components/ui/action-button";
import { Input } from "components/ui/input";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton, SecondaryCompactButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import React, { useEffect, useMemo, useState } from "react";

type RouterOutput = inferRouterOutputs<AppRouter>;
type GoalVersion = RouterOutput["marketingGoal"]["marketerVersions"][number];

export interface EditorMarketer {
    id: UserId;
    name: string;
}

interface GoalEditorProps {
    fixedMarketerId: UserId | null;
    marketers: EditorMarketer[];
    now: Date;
    onSaved: (effectiveMonth: string) => void;
    onCancel: () => void;
}

const goalGroups: { title: string; metrics: GoalMetric[] }[] = [
    { title: "Activity goals", metrics: activityGoalMetrics },
    { title: "Outcome goals", metrics: outcomeGoalMetrics },
];

export const resolveActiveVersion = (
    versions: GoalVersion[],
    month: string,
): GoalVersion | null => {
    let active: GoalVersion | null = null;
    for (const version of [...versions].sort((a, b) =>
        a.effectiveMonth.localeCompare(b.effectiveMonth),
    )) {
        if (version.effectiveMonth <= month) {
            active = version;
        }
    }
    return active;
};

const trackedFromVersion = (version: GoalVersion | null): Set<GoalMetric> => {
    if (!version) {
        return new Set(goalMetrics);
    }
    const tracked = goalMetrics.filter(metric => version[metric] > 0);
    return new Set(tracked.length ? tracked : goalMetrics);
};

const targetsFromVersion = (version: GoalVersion | null): MarketingGoalTargets => {
    const out = zeroTargets();
    if (version) {
        for (const metric of goalMetrics) {
            out[metric] = version[metric];
        }
    }
    return out;
};

export const GoalEditorPanel: React.FC<GoalEditorProps> = ({
    fixedMarketerId,
    marketers,
    now,
    onSaved,
    onCancel,
}) => {
    const isEdit = !!fixedMarketerId;
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();
    const currentMonth = currentYearMonth(now);
    const [selectedId, setSelectedId] = useState<UserId | null>(fixedMarketerId);
    const [applyMonth, setApplyMonth] = useState<string>(currentMonth);
    const [vals, setVals] = useState<MarketingGoalTargets>(zeroTargets());
    const [tracked, setTracked] = useState<Set<GoalMetric>>(new Set(goalMetrics));
    const [saveError, setSaveError] = useState<string | null>(null);

    const monthOptions = useMemo(
        () => Array.from({ length: 7 }, (_, index) => ymAddMonths(currentMonth, index)),
        [currentMonth],
    );

    const versionsQuery = useQuery({
        ...trpcClient.marketingGoal.marketerVersions.queryOptions({
            marketerUserId: selectedId ?? ("" as UserId),
        }),
        enabled: !!selectedId,
    });

    const upsertMutation = useMutation({
        ...trpcClient.marketingGoal.upsert.mutationOptions(),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: trpcClient.marketingGoal.teamAttainment.queryKey(),
            });
            await queryClient.invalidateQueries({
                queryKey: trpcClient.marketingGoal.marketerVersions.queryKey(),
            });
            onSaved(applyMonth);
        },
        onError: () => setSaveError("Couldn't save goals. Please try again."),
    });

    useEffect(() => {
        const available = versionsQuery.data ?? [];
        const active = selectedId ? resolveActiveVersion(available, applyMonth) : null;
        setVals(targetsFromVersion(active));
        setTracked(trackedFromVersion(active));
        setSaveError(null);
    }, [selectedId, applyMonth, versionsQuery.data]);

    const setVal = (metric: GoalMetric, raw: string) =>
        setVals(prev => ({
            ...prev,
            [metric]: Math.max(0, parseInt(raw || "0", 10) || 0),
        }));

    const removeMetric = (metric: GoalMetric) =>
        setTracked(prev => {
            const next = new Set(prev);
            next.delete(metric);
            return next;
        });

    const addMetric = (metric: GoalMetric) =>
        setTracked(prev => {
            const next = new Set(prev);
            next.add(metric);
            return next;
        });

    const total = goalMetrics.reduce(
        (sum, metric) => sum + (tracked.has(metric) ? vals[metric] : 0),
        0,
    );
    const canSave = !!selectedId && total > 0 && !upsertMutation.isPending;

    const handleSave = () => {
        if (!selectedId) {
            return;
        }
        upsertMutation.mutate({
            marketerUserId: selectedId,
            effectiveMonth: applyMonth,
            visit: tracked.has("visit") ? vals.visit : 0,
            call: tracked.has("call") ? vals.call : 0,
            text: tracked.has("text") ? vals.text : 0,
            email: tracked.has("email") ? vals.email : 0,
            referrals: tracked.has("referrals") ? vals.referrals : 0,
            careStarts: tracked.has("careStarts") ? vals.careStarts : 0,
        });
    };

    return (
        <Panel
            isOpen={true}
            onDismiss={onCancel}
            type={PanelType.large}
            headerText={isEdit ? "Edit goals" : "Create a goal"}>
            <div className="space-y-5">
                {!isEdit ? (
                    <Select
                        label="Marketer"
                        textSize="sm"
                        value={selectedId ?? ""}
                        onChange={event =>
                            setSelectedId((event.target.value || null) as UserId | null)
                        }>
                        <option value="">Select a marketer…</option>
                        {marketers.map(marketer => (
                            <option key={marketer.id} value={marketer.id}>
                                {marketer.name}
                            </option>
                        ))}
                    </Select>
                ) : null}

                <div className={selectedId ? "space-y-5" : "space-y-5 opacity-50"}>
                    {goalGroups.map(group => {
                        const shown = group.metrics.filter(metric => tracked.has(metric));
                        const removed = group.metrics.filter(
                            metric => !tracked.has(metric),
                        );
                        return (
                            <div key={group.title}>
                                <div className="mb-1.5 text-sm font-semibold text-zinc-700">
                                    {group.title}
                                </div>
                                <div className="divide-y divide-zinc-100 overflow-hidden rounded-lg ring-1 ring-zinc-200">
                                    {shown.length === 0 ? (
                                        <div className="px-3 py-2.5 text-sm text-zinc-400">
                                            None added.
                                        </div>
                                    ) : null}
                                    {shown.map(metric => (
                                        <div
                                            key={metric}
                                            className="flex items-center gap-3 bg-white px-3 py-2">
                                            <span
                                                className={`inline-block size-2.5 flex-shrink-0 rounded-sm ${goalMetricBarColor[metric]}`}
                                            />
                                            <span className="flex-1 text-sm text-zinc-800">
                                                {goalMetricLabels[metric]}
                                            </span>
                                            <Input
                                                type="number"
                                                min="0"
                                                textSize="sm"
                                                className="w-24"
                                                disabled={!selectedId}
                                                value={String(vals[metric])}
                                                onChange={event =>
                                                    setVal(metric, event.target.value)
                                                }
                                            />
                                            <DeleteButton
                                                backgroundColor="bg-white"
                                                onClick={() => removeMetric(metric)}
                                            />
                                        </div>
                                    ))}
                                    {removed.length > 0 ? (
                                        <div className="flex flex-wrap gap-2 bg-zinc-50/60 px-3 py-2">
                                            {removed.map(metric => (
                                                <SecondaryCompactButton
                                                    key={metric}
                                                    disabled={!selectedId}
                                                    onClick={() => addMetric(metric)}>
                                                    + {goalMetricLabels[metric]}
                                                </SecondaryCompactButton>
                                            ))}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        );
                    })}

                    <Select
                        label="Apply starting"
                        textSize="sm"
                        className="max-w-xs"
                        disabled={!selectedId}
                        value={applyMonth}
                        onChange={event => setApplyMonth(event.target.value)}>
                        {monthOptions.map(month => (
                            <option key={month} value={month}>
                                {ymLabel(month)}
                                {month === currentMonth ? " (this month)" : ""}
                            </option>
                        ))}
                    </Select>
                </div>

                {saveError ? (
                    <div className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700 ring-1 ring-rose-200">
                        {saveError}
                    </div>
                ) : null}

                <div className="flex items-center justify-end gap-2 border-t border-zinc-100 pt-4">
                    <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
                    <PrimaryButton disabled={!canSave} onClick={handleSave}>
                        {isEdit ? "Save goals" : "Create goal"}
                    </PrimaryButton>
                </div>
            </div>
        </Panel>
    );
};
